import { CONFIDENCE_BADGE_COLOR_MAP, ELASTICITY_GAUGE_SEGMENTS } from '../constants';
import type {
  ChartHighlightPoint,
  ChartIndicator,
  ChartSeries,
  CompetitorChartSeries,
  CompetitorCurveResponse,
  Confianca,
  CurveDataResponse,
  ElasticityCurveApiResponse,
  ElasticityResponse,
  MarketShareCompetitorMode,
  MarketShareResponse,
  ProdutoReferencia,
} from '../types';

const sanitizeCompetitorDisplayName = (name?: string | null): string => {
  if (!name) return '';

  const hyphenSeparatedParts = name
    .split(/\s*-\s*/)
    .map(part => part.trim())
    .filter(Boolean);

  const filteredParts = hyphenSeparatedParts.filter(part => !/^\d{8,14}$/.test(part));

  if (filteredParts.length > 0) {
    return filteredParts.join(' - ');
  }

  return name
    .replace(/\b\d{8,14}\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

/**
 * Converte elasticidade vinda da API (número ou string).
 * `parseFloat("0,01")` retorna 0 no JS — strings pt-BR com vírgula decimal precisam de tratamento.
 */
export const parseElasticityApiValue = (raw: string | number | undefined | null): number => {
  if (raw == null || raw === '') return NaN;
  if (typeof raw === 'number') {
    return Number.isFinite(raw) ? raw : NaN;
  }
  const s = String(raw).trim().replace(/^\+/, '');
  if (!s) return NaN;
  // Decimal BR: "0,01", "-0,12", "10,456"
  if (/^-?\d+,\d+$/.test(s)) {
    return parseFloat(s.replace(',', '.'));
  }
  // Inteiro com milhares BR "1.234" (sem parte decimal)
  if (/^-?\d{1,3}(\.\d{3})+$/.test(s)) {
    return parseFloat(s.replace(/\./g, ''));
  }
  const n = parseFloat(s.replace(',', '.'));
  return Number.isFinite(n) ? n : NaN;
};

const normalizeConfidence = (confidence?: string): string => {
  return confidence?.trim().toLocaleLowerCase('pt-BR') || '';
};

const confidenceLabel = (confidence?: string): Confianca => {
  return confidence?.trim() || '';
};

const confidenceBadgeColor = (confidence?: string): string => {
  const normalized = normalizeConfidence(confidence);
  return CONFIDENCE_BADGE_COLOR_MAP[normalized] ?? CONFIDENCE_BADGE_COLOR_MAP['confiável'];
};

const normalizeElasticityPointerValue = (elasticityValue: number) => {
  if (!Number.isFinite(elasticityValue) || elasticityValue <= 0) {
    return 0;
  }

  for (let index = 0; index < ELASTICITY_GAUGE_SEGMENTS.length; index += 1) {
    const segment = ELASTICITY_GAUGE_SEGMENTS[index];
    const normalizedFrom = index;
    const normalizedTo = index + 1;

    if (elasticityValue <= segment.to) {
      const segmentRange = segment.to - segment.from;

      if (segmentRange <= 0) {
        return normalizedFrom;
      }

      const clampedValue = Math.max(segment.from, elasticityValue);
      const progress = (clampedValue - segment.from) / segmentRange;

      return normalizedFrom + progress * (normalizedTo - normalizedFrom);
    }
  }

  return ELASTICITY_GAUGE_SEGMENTS.length;
};

const formatPercentageDifference = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value);
};

const normalizeReferencePoints = (curveData: CurveDataResponse) => {
  return (curveData.reference?.points || []).slice().sort((a, b) => a.x - b.x);
};

const toChartShareComp = (shareComp?: number) => {
  if (typeof shareComp !== 'number') return undefined;
  const normalized = Math.abs(shareComp) <= 1 ? shareComp * 100 : shareComp;
  return parseFloat(normalized.toFixed(2));
};

const normalizeSharePercent = (value?: number | null) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 0;
  return Math.abs(value) <= 1 ? value * 100 : value;
};

const roundUpAxisMax = (value: number) => {
  if (!Number.isFinite(value) || value <= 0) return 10;
  return Math.ceil(value / 10) * 10;
};

const interpolateSeriesValue = (points: { x: number; y: number }[], x: number): number | null => {
  if (!points.length) return null;

  const exactPoint = points.find(point => Math.abs(point.x - x) < 1e-9);

  if (exactPoint) {
    return exactPoint.y;
  }

  const sortedPoints = points.slice().sort((a, b) => a.x - b.x);

  if (x <= sortedPoints[0].x) return sortedPoints[0].y;
  if (x >= sortedPoints[sortedPoints.length - 1].x) return sortedPoints[sortedPoints.length - 1].y;

  for (let index = 0; index < sortedPoints.length - 1; index += 1) {
    const currentPoint = sortedPoints[index];
    const nextPoint = sortedPoints[index + 1];

    if (x < currentPoint.x || x > nextPoint.x) {
      continue;
    }

    const range = nextPoint.x - currentPoint.x;

    if (range === 0) {
      return currentPoint.y;
    }

    const progress = (x - currentPoint.x) / range;
    return parseFloat((currentPoint.y + (nextPoint.y - currentPoint.y) * progress).toFixed(2));
  }

  return null;
};

const buildHighlightPoint = (
  x: number,
  kind: ChartHighlightPoint['kind'],
  valuePoints: { x: number; y: number }[],
  volumePoints: { x: number; y: number }[],
  gain?: number | null,
): ChartHighlightPoint => ({
  x,
  kind,
  valueShare: interpolateSeriesValue(valuePoints, x),
  volumeShare: interpolateSeriesValue(volumePoints, x),
  gain: kind === 'optimal' ? (gain ?? null) : null,
});

const buildMarketShareSeries = (curveData: CurveDataResponse, metric: 'VOLUME' | 'VALOR', color: string): ChartSeries => {
  const filteredReferencePoints = normalizeReferencePoints(curveData);

  return {
    metric,
    name: metric === 'VOLUME' ? 'Share por Volume' : 'Share por Vendas',
    color,
    data: filteredReferencePoints.map(point => ({
      x: point.x,
      y: parseFloat(normalizeSharePercent(point.y).toFixed(3)),
      shareComp: toChartShareComp(point.shareComp),
      isBullet: point.isBullet,
    })),
    bulletData: filteredReferencePoints
      .filter(point => point.isBullet)
      .map(point => ({
        x: point.x,
        y: parseFloat(normalizeSharePercent(point.y).toFixed(3)),
        shareComp: toChartShareComp(point.shareComp),
        isBullet: point.isBullet,
      })),
  };
};

const COMPETITOR_COLORS = ['#FF8C00', '#8B5CF6', '#10B981', '#F43F5E', '#06B6D4', '#F59E0B'];

const buildCompetitorSeriesFromCurve = (competitor: CompetitorCurveResponse, color: string, metric: 'VOLUME' | 'VALOR'): ChartSeries => {
  const sortedPoints = (competitor.points || []).slice().sort((a, b) => a.x - b.x);

  return {
    metric,
    name: sanitizeCompetitorDisplayName(competitor.name) || competitor.barcode || 'Concorrente',
    color,
    data: sortedPoints.map(point => ({
      x: point.x,
      y: parseFloat(normalizeSharePercent(point.y).toFixed(3)),
      shareComp: toChartShareComp(point.shareComp),
      isBullet: false,
    })),
  };
};

// competitorSeries
const buildAverageCompetitorSeries = (competitors: CompetitorCurveResponse[], color: string, metric: 'VOLUME' | 'VALOR'): CompetitorChartSeries[] => {
  if (!competitors.length) return [];

  const sanitizedNames = competitors.map(competitor => sanitizeCompetitorDisplayName(competitor.name)).filter(Boolean);

  const uniqueNames = Array.from(new Set(sanitizedNames));
  const averageSeriesName = uniqueNames.length ? `MÉDIA (${uniqueNames.join(', ')})` : 'MÉDIA';
  const xValues = Array.from(
    new Set(
      competitors.flatMap(competitor =>
        (competitor.points || []).map(point => point.x).filter((x): x is number => typeof x === 'number' && Number.isFinite(x)),
      ),
    ),
  ).sort((firstValue, secondValue) => firstValue - secondValue);

  const data = xValues
    .map(x => {
      const pointsAtX = competitors
        .map(competitor =>
          interpolateSeriesValue(
            (competitor.points || []).map(point => ({ x: point.x, y: normalizeSharePercent(point.y) })),
            x,
          ),
        )
        .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));

      if (!pointsAtX.length) return null;

      const averageValue = pointsAtX.reduce((accumulator, value) => accumulator + value, 0) / pointsAtX.length;

      return {
        x,
        y: parseFloat(averageValue.toFixed(2)),
        isBullet: false,
      };
    })
    .filter((point): point is NonNullable<typeof point> => point !== null);

  if (!data.length) return [];

  return [
    {
      competitorName: averageSeriesName,
      series: {
        metric,
        name: averageSeriesName,
        color,
        data,
      },
    },
  ];
};

// Constroi as séries de competidores por tipo de vendas (VALOR ou VOLUME)
const buildCompetitorChartSeries = (apiResponse: ElasticityCurveApiResponse): { revenue: CompetitorChartSeries[]; volume: CompetitorChartSeries[] } => {
  const revenueCompetitors = apiResponse.data.salesRevenue.competitors || [];
  const volumeCompetitors = apiResponse.data.salesVolume.competitors || [];

  const competitors = {
    revenue: revenueCompetitors.map((comp, idx) => ({
      competitorName: sanitizeCompetitorDisplayName(comp.name) || comp.barcode || `Concorrente ${idx + 1}`,
      series: buildCompetitorSeriesFromCurve(comp, COMPETITOR_COLORS[idx % COMPETITOR_COLORS.length], 'VALOR'),
    })),
    volume: volumeCompetitors.map((comp, idx) => ({
      competitorName: sanitizeCompetitorDisplayName(comp.name) || comp.barcode || `Concorrente ${idx + 1}`,
      series: buildCompetitorSeriesFromCurve(comp, COMPETITOR_COLORS[idx % COMPETITOR_COLORS.length], 'VOLUME'),
    })),
  };

  return competitors;
};

const buildCompetitorAverageSeriesByMetric = (
  apiResponse: ElasticityCurveApiResponse,
): { revenue: CompetitorChartSeries[]; volume: CompetitorChartSeries[] } => ({
  revenue: buildAverageCompetitorSeries(apiResponse.data.salesRevenue.competitors || [], COMPETITOR_COLORS[0], 'VALOR'),
  volume: buildAverageCompetitorSeries(apiResponse.data.salesVolume.competitors || [], COMPETITOR_COLORS[0], 'VOLUME'),
});

const buildChartIndicators = (apiResponse: ElasticityCurveApiResponse): ChartIndicator[] => {
  const indicators: ChartIndicator[] = [];
  const cpiInfopanel = apiResponse.data.cpiInfopanel;
  const revenueOptimalCpi = apiResponse.data.salesRevenue.optimalCpi;

  if (cpiInfopanel?.show) {
    indicators.push({
      key: 'cpiInfopanel',
      name: 'Competitividade Infopanel atual',
      color: '#D3D3D3',
      x: cpiInfopanel.x,
      y: parseFloat(normalizeSharePercent(cpiInfopanel.y).toFixed(3)),
      shareComp: parseFloat(normalizeSharePercent(cpiInfopanel.shareComp).toFixed(2)),
      show: true,
    });
  }

  if (revenueOptimalCpi?.show) {
    const optimalCpi = revenueOptimalCpi;
    indicators.push({
      key: 'optimalCpi',
      metric: 'VALOR',
      name: 'Competitividade ótima',
      color: '#33AB84',
      x: optimalCpi.x,
      y: parseFloat(normalizeSharePercent(optimalCpi.y).toFixed(3)),
      shareComp: parseFloat(normalizeSharePercent(optimalCpi.shareComp).toFixed(2)),
      show: true,
    });
  }

  return indicators;
};

export const mapApiToElasticityResponse = (
  apiResponse: ElasticityCurveApiResponse,
  curveType: 'salesVolume' | 'salesRevenue' = 'salesVolume',
): ElasticityResponse => {
  const curveData = apiResponse.data[curveType];
  const parsedElasticity = parseElasticityApiValue(curveData.elasticity.value);
  const elasticityValue = Number.isFinite(parsedElasticity) ? parsedElasticity : 0;
  const classification = curveData.elasticity.level?.trim() || 'Não identificado';

  return {
    success: true,
    data: {
      elasticity: elasticityValue,
      classification,
      description: 'Pequenas variações de preço impactam nas vendas do produto',
      dataPoints: curveData.reference?.points?.length || 0,
      // Alinhado à homologação do CPI (data-plataform weekly_cpi_calculator): janela móvel ~30 dias.
      // A API de curvas não envia datas; não usar este bloco como “período do modelo MNL” (gauge/curva).
      period: {
        weeks: Math.round((30 / 7) * 10) / 10,
        startDate: (() => {
          const d = new Date();
          d.setDate(d.getDate() - 30);
          return d.toISOString().split('T')[0];
        })(),
        endDate: new Date().toISOString().split('T')[0],
      },
      gaugeConfig: {
        min: 0,
        max: 4,
        pointerValue: normalizeElasticityPointerValue(elasticityValue),
        plotBands: [
          { from: 0, to: 1, color: '#E8F4FD', label: 'Inelástico' },
          { from: 1, to: 2, color: '#B3D9F7', label: 'Pouco Elástico' },
          { from: 2, to: 3, color: '#559FF2', label: 'Elástico' },
          { from: 3, to: 4, color: '#0D66D0', label: 'Muito Elástico' },
        ],
      },
    },
  };
};

export const mapApiToMarketShareResponse = (
  apiResponse: ElasticityCurveApiResponse,
  produtoReferenciaInfo?: ProdutoReferencia | null,
  competitorMode: MarketShareCompetitorMode = 'INDIVIDUAL',
): MarketShareResponse => {
  const volumeSeries = buildMarketShareSeries(apiResponse.data.salesVolume, 'VOLUME', '#0D66D0');
  const valueSeries = buildMarketShareSeries(apiResponse.data.salesRevenue, 'VALOR', '#E663C9');
  const graph = apiResponse.data.graph;
  const cpi = apiResponse.data.cpiInfopanel;
  const confidence = apiResponse.data.confidence;
  const revenueOptimalCpi = apiResponse.data.salesRevenue.optimalCpi;
  const bulletPoints = [...(volumeSeries.bulletData || []), ...(valueSeries.bulletData || [])];
  const axisPoints = bulletPoints.length ? bulletPoints : [...volumeSeries.data, ...valueSeries.data];
  const markerXs: number[] = [];
  if (cpi?.show && typeof cpi.x === 'number' && Number.isFinite(cpi.x)) {
    markerXs.push(cpi.x);
  }
  if (revenueOptimalCpi?.show && typeof revenueOptimalCpi.x === 'number' && Number.isFinite(revenueOptimalCpi.x)) {
    markerXs.push(revenueOptimalCpi.x);
  }
  const graphSpan = graph['max-x'] - graph['min-x'];
  const axisPad = graphSpan > 0 ? graphSpan * 0.02 : 0.02;
  const xAxisMin = Math.min(graph['min-x'], ...(markerXs.length ? markerXs : [graph['min-x']])) - axisPad;
  const xAxisMax = Math.max(graph['max-x'], ...(markerXs.length ? markerXs : [graph['max-x']])) + axisPad;
  const xValues = Array.from(new Set([...axisPoints.map(point => point.x), ...markerXs, xAxisMin, xAxisMax])).sort((a, b) => a - b);
  const indicators = buildChartIndicators(apiResponse);
  const competitorSeriesByMetric = buildCompetitorChartSeries(apiResponse);
  const competitorAverageSeriesByMetric = buildCompetitorAverageSeriesByMetric(apiResponse);
  const currentHighlight = cpi ? buildHighlightPoint(cpi.x, 'current', valueSeries.data, volumeSeries.data) : null;
  const optimalBaseHighlight = revenueOptimalCpi?.show ? buildHighlightPoint(revenueOptimalCpi.x, 'optimal', valueSeries.data, volumeSeries.data) : null;
  const potentialGain =
    currentHighlight && optimalBaseHighlight ? Math.max(0, (optimalBaseHighlight.valueShare ?? 0) - (currentHighlight.valueShare ?? 0)) : null;
  const optimalHighlight = optimalBaseHighlight
    ? {
        ...optimalBaseHighlight,
        gain: potentialGain,
      }
    : null;
  const highlightMap = new Map<string, ChartHighlightPoint>();

  bulletPoints.forEach(point => {
    highlightMap.set(point.x.toFixed(4), buildHighlightPoint(point.x, 'bullet', valueSeries.data, volumeSeries.data));
  });

  if (currentHighlight) {
    highlightMap.set(currentHighlight.x.toFixed(4), currentHighlight);
  }

  if (optimalHighlight) {
    highlightMap.set(optimalHighlight.x.toFixed(4), optimalHighlight);
  }

  const pctDiff = parseFloat(((cpi.current - 1) * 100).toFixed(1));

  return {
    success: true,
    data: {
      client: apiResponse.client,
      chart: {
        title: 'Projeção de market share',
        xAxis: {
          title: 'CPI do produto selecionado VS concorrência',
          min: xAxisMin,
          max: xAxisMax,
          tickPositions: xValues,
        },
        yAxis: {
          title: 'Share de vendas',
          suffix: '%',
          max: roundUpAxisMax(parseFloat(normalizeSharePercent(graph['max-y']).toFixed(3))),
          maxByMetric: {
            VALOR: roundUpAxisMax(parseFloat(normalizeSharePercent(graph['max-y-revenue']).toFixed(3))),
            VOLUME: roundUpAxisMax(parseFloat(normalizeSharePercent(graph['max-y-volume']).toFixed(3))),
          },
        },
        series: [volumeSeries, valueSeries],
        competitorSeries: competitorMode === 'AVERAGE' ? [...competitorAverageSeriesByMetric.volume, ...competitorAverageSeriesByMetric.revenue] : [],
        competitorAverageSeriesByMetric,
        competitorSeriesByMetric,
        indicators,
      },
      competitividade: {
        valor: parseFloat((cpi.current * 100).toFixed(1)),
        percentual: Math.abs(pctDiff),
        descricao:
          pctDiff > 0
            ? `Está em média ${formatPercentageDifference(Math.abs(pctDiff))}% mais caro`
            : `Está em média ${formatPercentageDifference(Math.abs(pctDiff))}% mais barato`,
        confianca: confidenceLabel(confidence),
        badgeColor: confidenceBadgeColor(confidence),
      },
      produtoReferencia: produtoReferenciaInfo ?? null,
      insights: {
        points: Array.from(highlightMap.values()).sort((firstPoint, secondPoint) => firstPoint.x - secondPoint.x),
        current: currentHighlight,
        optimal: optimalHighlight,
        potentialGain,
      },
    },
  };
};
